import * as React from 'react';

import { DimeTooltip } from '../DimeTooltip.js';

type UseDisabledTooltipOptions = {
  disabled?: boolean;
  tooltip?: string;
  // True when the wrapped child expects to fill its container's width (e.g. has `w-full`).
  fullWidth?: boolean;
};

// A disabled control has pointer-events-none and is excluded from the tab order, so a
// tooltip trigger can't live on the control itself; wrap in a non-disabled, focusable
// span that Radix can attach hover/focus listeners to instead.
export function useDisabledTooltip(
  { disabled, tooltip, fullWidth }: UseDisabledTooltipOptions,
  children: React.ReactNode,
) {
  const descriptionId = React.useId();
  if (!disabled || !tooltip) return children;
  const describedChild = React.isValidElement<{ 'aria-describedby'?: string }>(children)
    ? React.cloneElement(children, {
        'aria-describedby': [children.props['aria-describedby'], descriptionId]
          .filter(Boolean)
          .join(' '),
      })
    : children;
  return (
    <DimeTooltip content={tooltip} openOnTap>
      <span
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a disabled button takes no focus, so its tooltip needs a focusable wrapper
        tabIndex={0}
        data-slot="disabled-tooltip"
        className={fullWidth ? 'flex w-full' : 'inline-flex'}
      >
        {describedChild}
        <span id={descriptionId} className="sr-only">
          {tooltip}
        </span>
      </span>
    </DimeTooltip>
  );
}
